import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, Tablet, X, Clock, MapPin, Wifi, CheckCircle, XCircle } from 'lucide-react';

interface DeviceInfo {
    browser: string;
    os: string;
    platform: string;
    vendor: string | null;
    model: string | null;
    deviceName: string | null;
}

interface SessionData {
    sessionId: string;
    deviceInfo: DeviceInfo;
    ipAddress: string;
    createdAt: string;
    lastActivityAt: string;
    isActive: boolean;
}

interface Props {
    userId: string;
    username: string;
}

const getStatusColor = (count: number) => {
    if (count <= 3) return '#16a34a';
    if (count <= 5) return '#d97706';
    return '#dc2626';
};

const getDeviceIcon = (platform: string) => {
    if (platform === 'mobile') return <Smartphone size={18} />;
    if (platform === 'tablet') return <Tablet size={18} />;
    return <Monitor size={18} />;
};

const getDeviceLabel = (info: DeviceInfo): string => {
    if (info.vendor && info.model) return `${info.vendor} ${info.model}`;
    if (info.deviceName && info.deviceName !== 'Computador') return info.deviceName;
    return info.browser || 'Dispositivo desconhecido';
};

const formatDate = (d: string) =>
    new Date(d).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });

const UserSessionsButton: React.FC<Props> = ({ userId, username }) => {
    const [open, setOpen] = useState(false);
    const [sessions, setSessions] = useState<SessionData[]>([]);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState<number | null>(null);

    // ✅ Carregar contagem automaticamente ao montar
    useEffect(() => {
        const fetchCount = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`/api/admin/users/${userId}/sessions/history`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                setCount(data.total ?? 0);
            } catch {
                setCount(0);
            }
        };
        fetchCount();
    }, [userId]);

    const fetchSessions = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/users/${userId}/sessions/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            setSessions(data.sessions || []);
            setCount(data.total ?? 0);
        } catch {
            setSessions([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = async () => {
        setOpen(true);
        await fetchSessions();
    };

    const handleClose = () => setOpen(false);

    const color = count !== null ? getStatusColor(count) : '#6B7280';

    return (
        <>
            {/* Botão inline — já colorido desde o mount */}
            <button
                onClick={handleOpen}
                title="Ver histórico de dispositivos"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 9px',
                    borderRadius: 20,
                    border: 'none',
                    background: color,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    marginLeft: 8,
                    verticalAlign: 'middle',
                    lineHeight: 1,
                    transition: 'background 0.3s ease, opacity 0.2s',
                    opacity: count === null ? 0.5 : 1,
                    fontFamily: 'inherit'
                }}
            >
                <Wifi size={11} />
                {count !== null ? count : '…'}
            </button>

            {/* Modal */}
            {open && (
                <div
                    onClick={handleClose}
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.55)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 16
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#fff',
                            borderRadius: 14,
                            width: '100%',
                            maxWidth: 520,
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '20px 24px 16px',
                            borderBottom: '1px solid #E5E7EB',
                            position: 'sticky',
                            top: 0,
                            background: '#fff',
                            zIndex: 1
                        }}>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>
                                    Histórico de dispositivos
                                </div>
                                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                                    {username} · últimos 30 dias
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {count !== null && (
                                    <span style={{
                                        padding: '4px 12px',
                                        borderRadius: 20,
                                        background: color,
                                        color: '#fff',
                                        fontSize: 12,
                                        fontWeight: 700
                                    }}>
                                        {count} {count === 1 ? 'dispositivo' : 'dispositivos'}
                                    </span>
                                )}
                                <button
                                    onClick={handleClose}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#6B7280',
                                        padding: 4,
                                        display: 'flex'
                                    }}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Legenda */}
                        <div style={{
                            display: 'flex', gap: 16, padding: '12px 24px',
                            background: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
                            fontSize: 12, color: '#6B7280', flexWrap: 'wrap',
                            alignItems: 'center'
                        }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <Wifi size={12} color="#16a34a" />
                                até 3 — normal
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <Wifi size={12} color="#d97706" />
                                4–5 — atenção
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <Wifi size={12} color="#dc2626" />
                                +5 — crítico
                            </span>
                        </div>

                        {/* Corpo */}
                        <div style={{ padding: '16px 24px 24px' }}>
                            {loading && (
                                <div style={{ textAlign: 'center', padding: '32px 0', color: '#6B7280', fontSize: 14 }}>
                                    Carregando...
                                </div>
                            )}

                            {!loading && sessions.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 14 }}>
                                    Nenhum dispositivo encontrado nos últimos 30 dias
                                </div>
                            )}

                            {!loading && sessions.map((s, i) => (
                                <div
                                    key={s.sessionId}
                                    style={{
                                        display: 'flex',
                                        gap: 14,
                                        alignItems: 'flex-start',
                                        padding: '14px 0',
                                        borderBottom: i < sessions.length - 1 ? '1px solid #F3F4F6' : 'none'
                                    }}
                                >
                                    {/* Ícone dispositivo */}
                                    <div style={{
                                        width: 40, height: 40,
                                        background: s.isActive ? '#F0FDF4' : '#F3F4F6',
                                        borderRadius: 8,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: s.isActive ? '#003BD1' : '#9CA3AF',
                                        flexShrink: 0
                                    }}>
                                        {getDeviceIcon(s.deviceInfo.platform)}
                                    </div>

                                    {/* Info */}
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontWeight: 600, fontSize: 14,
                                            color: '#111827', marginBottom: 4,
                                            display: 'flex', alignItems: 'center', gap: 8
                                        }}>
                                            {getDeviceLabel(s.deviceInfo)}
                                            {/* Badge ativo/inativo */}
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                                fontSize: 11, fontWeight: 600,
                                                color: s.isActive ? '#16a34a' : '#6B7280'
                                            }}>
                                                {s.isActive
                                                    ? <><CheckCircle size={11} /> ativo</>
                                                    : <><XCircle size={11} /> encerrado</>
                                                }
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 13, color: '#6B7280', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <Monitor size={12} />
                                                {s.deviceInfo.browser} · {s.deviceInfo.os}
                                            </span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <MapPin size={12} />
                                                {s.ipAddress || 'IP não disponível'}
                                            </span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <Clock size={12} />
                                                Última atividade: {formatDate(s.lastActivityAt)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserSessionsButton;
